import type { FaqContent, IconName, LinkItem } from './types';

/*
 * Content for the Partnerships product page (URL /partnerships).
 * Copy is adapted from nsave.com/products/partnerships onto this site's
 * components. External links (application forms + resource PDFs) point at the
 * live nsave assets so the page is functional.
 */

export const partnershipsHero: {
  title: string;
  /** Primary CTA opens the general partner application form (external). */
  cta: LinkItem;
} = {
  title: 'Join the partnership\nprogram and grow with\nnsave',
  cta: {
    label: 'Become a partner',
    href: 'https://forms.gle/QqzKLskWRdwzg2uJ7',
    external: true,
  },
};

/**
 * Decorative marketing clips shown in a 5-wide grid between the hero and the
 * "Join the movement" section. Portrait (640×920) autoplay-muted-loop videos
 * served from `public/videos/partnerships/`.
 */
export const partnershipsVideos: { src: string }[] = [
  { src: '/videos/partnerships/partner-1.mp4' },
  { src: '/videos/partnerships/partner-2.mp4' },
  { src: '/videos/partnerships/partner-3.mp4' },
  { src: '/videos/partnerships/partner-4.mp4' },
  { src: '/videos/partnerships/partner-5.mp4' },
];

export interface PartnershipType {
  icon: IconName;
  title: string;
  description: string;
  /** "Apply as …" link to the relevant application form (external). */
  cta: LinkItem;
}

export const partnershipTypes: {
  heading: string;
  items: PartnershipType[];
} = {
  heading: 'Join the movement.\nMake an impact.',
  items: [
    {
      icon: 'user',
      title: 'Influencer',
      description:
        'Share nsave with your audience through reels, videos, posts, or other creative content.',
      cta: {
        label: 'Apply as influencer',
        href: 'https://forms.gle/QqzKLskWRdwzg2uJ7',
        external: true,
      },
    },
    {
      icon: 'users',
      title: 'Communities',
      description:
        'Give your members access to financial tools with dedicated workshops, posts, groups or shared resources.',
      cta: {
        label: 'Apply as community',
        href: 'https://forms.gle/QqzKLskWRdwzg2uJ7',
        external: true,
      },
    },
    {
      icon: 'at-sign',
      title: 'Brand / Platform',
      description:
        'Integrate nsave into your ecosystem to offer users seamless multi-currency payouts and transfers.',
      cta: {
        label: 'Apply as brand',
        href: 'https://forms.gle/VHGrMKKFjqmm3f9a8',
        external: true,
      },
    },
  ],
};

export interface PromoteItem {
  icon: IconName;
  title: string;
  description: string;
}

export const partnershipsPromote: {
  title: string;
  body: string;
  /** Downloadable partner resources (external PDFs). */
  resources: LinkItem[];
  items: PromoteItem[];
} = {
  title: 'What can I promote?',
  body: 'Choose what matters most to your audience. From our secure USD accounts abroad and quick global transfers to sending money home, investing, or our dedicated business tools. Promote one feature or the full suite. You know your community best.',
  resources: [
    {
      label: 'Product Offering',
      href: 'https://cdn.prod.website-files.com/66de9fe4a74bb719d845a843/69948783ecf99d3f540c53f2_Value%20Proposition%2016-02.pdf',
      external: true,
    },
    {
      label: 'Brand & Content Guidelines',
      href: 'https://cdn.prod.website-files.com/66de9fe4a74bb719d845a843/69948276ad83d2852c41874d_Brand%20Guidelines%2014-02.pdf',
      external: true,
    },
  ],
  items: [
    {
      icon: 'bank',
      title: 'USD accounts abroad',
      description: 'Real USD, GBP and EUR account details in your audience’s name.',
    },
    {
      icon: 'swap',
      title: 'Quick global transfers',
      description: 'Fast cross-border transfers at the real exchange rate.',
    },
    {
      icon: 'globe',
      title: 'Send money home',
      description: 'Send to local currencies like PKR, BDT and EGP with one clear fee.',
    },
    {
      icon: 'chart',
      title: 'Investing',
      description: 'Invest in 5000+ U.S. stocks, ETFs, bonds and gold indices from $1.',
    },
    {
      icon: 'invoice',
      title: 'Business tools',
      description: 'Receive payments, pay invoices and run payroll from one account.',
    },
  ],
};

export const partnershipsFaq: FaqContent = {
  title: 'How it works',
  items: [
    {
      question: 'How does the nsave partnership work?',
      answer:
        'Becoming an nsave partner is simple. We provide everything you need — marketing materials, dedicated support, tracking tools, and benefits for you and your community. Here’s what you need to know.',
    },
    {
      question: 'Who can become an nsave partner?',
      answer:
        'We partner with anyone who has an audience in need of better financial tools. Whether you’re creating content, leading a community, or running a platform, if you care about helping your audience access reliable banking and money management, let’s talk.',
    },
    {
      question: 'How do partnerships work?',
      answer:
        'It depends on what makes sense for you and your audience. Influencers and communities typically earn per referral with a budget allocated to resources, and platforms work with us on custom benefits and revenue share. We’ll figure out the right structure together during the application process.',
    },
    {
      question: 'Do you provide marketing materials?',
      answer:
        'Yes. You get branded content, product guides, social assets, and messaging frameworks. Everything is in our partner portal, designed to help you share nsave authentically.',
    },
    {
      question: 'How do I track performance?',
      answer: 'Every partner gets dashboard access to see referrals and conversions.',
    },
    {
      question: 'What makes nsave different from other fintech partnerships?',
      answer:
        'We’re a complete financial platform for people in emerging markets. Your audience gets everything they need to earn, spend, and grow their money in USD and international currencies — all in one place. From personal accounts to business tools, we offer the best rates and functionality without the runaround. Plus, we’re backed by Y Combinator and Sequoia Capital, so you’re partnering with a company built to last.',
    },
  ],
};
